/**
 * Expense Error Handling Utilities
 * Provides specific error messages, error reporting, and recovery functions
 */

import { toast } from "react-toastify";

/**
 * Error Codes for Expense operations
 */
export const EXPENSE_ERROR_CODES = {
  // Network Errors
  NETWORK_ERROR: "EXPENSE_NETWORK_001",
  TIMEOUT_ERROR: "EXPENSE_NETWORK_002",
  
  // API Errors
  API_BAD_REQUEST: "EXPENSE_API_400",
  API_UNAUTHORIZED: "EXPENSE_API_401",
  API_FORBIDDEN: "EXPENSE_API_403",
  API_NOT_FOUND: "EXPENSE_API_404",
  API_CONFLICT: "EXPENSE_API_409",
  API_VALIDATION: "EXPENSE_API_422",
  API_RATE_LIMIT: "EXPENSE_API_429",
  API_SERVER_ERROR: "EXPENSE_API_500",
  API_SERVICE_UNAVAILABLE: "EXPENSE_API_503",
  
  // Business Logic Errors
  EXPENSE_CREATE_FAILED: "EXPENSE_CREATE_001",
  EXPENSE_UPDATE_FAILED: "EXPENSE_UPDATE_001",
  EXPENSE_DELETE_FAILED: "EXPENSE_DELETE_001",
  EXPENSE_FETCH_FAILED: "EXPENSE_FETCH_001",
  EXPENSE_EXPORT_FAILED: "EXPENSE_EXPORT_001",
  
  // Validation Errors
  MISSING_REQUIRED_FIELD: "EXPENSE_VALIDATION_001",
  INVALID_DATE: "EXPENSE_VALIDATION_002",
  INVALID_AMOUNT: "EXPENSE_VALIDATION_003",
  INVALID_FILE: "EXPENSE_VALIDATION_004",
  FILE_SIZE_EXCEEDED: "EXPENSE_VALIDATION_005",
  FILE_COUNT_EXCEEDED: "EXPENSE_VALIDATION_006",
  
  // Data Errors
  DATA_FETCH_ERROR: "EXPENSE_DATA_001",
  DATA_SAVE_ERROR: "EXPENSE_DATA_002",
  DATA_PARSE_ERROR: "EXPENSE_DATA_003",
  
  // Unknown Errors
  UNKNOWN_ERROR: "EXPENSE_UNKNOWN_001",
};

/**
 * Get specific error message based on error type and context
 * @param {Error|string} error - The error object or message
 * @param {string} context - The context where the error occurred
 * @param {Object} additionalInfo - Additional information about the error
 * @returns {Object} Error object with code, message, and details
 */
export const getErrorMessage = (error, context = "Expense operation", additionalInfo = {}) => {
  const errorCode = additionalInfo.code || EXPENSE_ERROR_CODES.UNKNOWN_ERROR;
  
  // Handle string errors
  if (typeof error === "string") {
    return {
      code: errorCode,
      message: `Error during ${context}: ${error}`,
      context,
      details: additionalInfo,
    };
  }

  // Handle Error objects
  if (error instanceof Error) {
    const errorMessage = error.message || error.toString();
    
    // Network errors
    if (errorMessage.includes("Network Error") || errorMessage.includes("timeout")) {
      return {
        code: EXPENSE_ERROR_CODES.NETWORK_ERROR,
        message: "Network error: Unable to connect to server. Please check your internet connection and try again.",
        context,
        details: { originalError: errorMessage, ...additionalInfo },
      };
    }

    // API errors with status codes
    if (error.response) {
      const status = error.response.status;
      const statusText = error.response.statusText;
      const responseData = error.response.data || {};
      
      switch (status) {
        case 400:
          return {
            code: EXPENSE_ERROR_CODES.API_BAD_REQUEST,
            message: `Invalid request: ${responseData.message || statusText}. Please check your input and try again.`,
            context,
            details: { status, responseData, ...additionalInfo },
          };
        case 401:
          return {
            code: EXPENSE_ERROR_CODES.API_UNAUTHORIZED,
            message: "Authentication failed: Your session may have expired. Please refresh the page and try again.",
            context,
            details: { status, responseData, ...additionalInfo },
          };
        case 403:
          return {
            code: EXPENSE_ERROR_CODES.API_FORBIDDEN,
            message: "Access denied: You don't have permission to perform this action.",
            context,
            details: { status, responseData, ...additionalInfo },
          };
        case 404:
          return {
            code: EXPENSE_ERROR_CODES.API_NOT_FOUND,
            message: `Resource not found: The requested ${context} could not be found.`,
            context,
            details: { status, responseData, ...additionalInfo },
          };
        case 409:
          return {
            code: EXPENSE_ERROR_CODES.API_CONFLICT,
            message: `Conflict: ${responseData.message || "This action conflicts with the current state."}`,
            context,
            details: { status, responseData, ...additionalInfo },
          };
        case 422:
          return {
            code: EXPENSE_ERROR_CODES.API_VALIDATION,
            message: `Validation error: ${responseData.message || "Please check your input data."}`,
            context,
            details: { status, responseData, ...additionalInfo },
          };
        case 429:
          return {
            code: EXPENSE_ERROR_CODES.API_RATE_LIMIT,
            message: "Too many requests: Please wait a moment and try again.",
            context,
            details: { status, responseData, ...additionalInfo },
          };
        case 500:
          return {
            code: EXPENSE_ERROR_CODES.API_SERVER_ERROR,
            message: "Server error: An internal server error occurred. Please try again later or contact support.",
            context,
            details: { status, responseData, ...additionalInfo },
          };
        case 503:
          return {
            code: EXPENSE_ERROR_CODES.API_SERVICE_UNAVAILABLE,
            message: "Service unavailable: The server is temporarily unavailable. Please try again later.",
            context,
            details: { status, responseData, ...additionalInfo },
          };
        default:
          return {
            code: `EXPENSE_API_${status}`,
            message: `Error ${status}: ${responseData.message || statusText || `An error occurred during ${context}`}`,
            context,
            details: { status, responseData, ...additionalInfo },
          };
      }
    }

    // localStorage errors
    if (errorMessage.includes("localStorage") || errorMessage.includes("Storage")) {
      return {
        code: EXPENSE_ERROR_CODES.DATA_SAVE_ERROR,
        message: `Storage error: Unable to save data locally. ${additionalInfo.action || "Please check your browser settings."}`,
        context,
        details: { originalError: errorMessage, ...additionalInfo },
      };
    }

    // JSON parsing errors
    if (errorMessage.includes("JSON") || errorMessage.includes("parse")) {
      return {
        code: EXPENSE_ERROR_CODES.DATA_PARSE_ERROR,
        message: `Data format error: Invalid data format encountered. ${additionalInfo.dataType || "Please refresh the page."}`,
        context,
        details: { originalError: errorMessage, ...additionalInfo },
      };
    }

    // File errors
    if (errorMessage.includes("File") || errorMessage.includes("file")) {
      if (errorMessage.includes("size") || errorMessage.includes("too large")) {
        return {
          code: EXPENSE_ERROR_CODES.FILE_SIZE_EXCEEDED,
          message: "File size error: The file is too large. Please select a smaller file.",
          context,
          details: { originalError: errorMessage, ...additionalInfo },
        };
      }
      if (errorMessage.includes("count") || errorMessage.includes("maximum")) {
        return {
          code: EXPENSE_ERROR_CODES.FILE_COUNT_EXCEEDED,
          message: "File count error: Maximum number of files exceeded. Please remove some files.",
          context,
          details: { originalError: errorMessage, ...additionalInfo },
        };
      }
      return {
        code: EXPENSE_ERROR_CODES.INVALID_FILE,
        message: "File error: Invalid file format or corrupted file. Please select a valid file.",
        context,
        details: { originalError: errorMessage, ...additionalInfo },
      };
    }

    // Default error message
    return {
      code: errorCode,
      message: `Error during ${context}: ${errorMessage}`,
      context,
      details: { originalError: errorMessage, ...additionalInfo },
    };
  }

  // Fallback for unknown error types
  return {
    code: EXPENSE_ERROR_CODES.UNKNOWN_ERROR,
    message: `An unexpected error occurred during ${context}. Please try again.`,
    context,
    details: additionalInfo,
  };
};

/**
 * Log error with context and send to Slack if configured
 * @param {Error|string} error - The error to log
 * @param {string} context - The context where the error occurred
 * @param {Object} additionalInfo - Additional information
 * @returns {Object} Error object with code and message
 */
export const handleError = (error, context = "Expense operation", additionalInfo = {}) => {
  const errorInfo = getErrorMessage(error, context, additionalInfo);
  
  // Log to console with full error details
  console.error(`[Expense - ${context}]`, {
    errorCode: errorInfo.code,
    error: error instanceof Error ? error : new Error(error),
    message: errorInfo.message,
    context: errorInfo.context,
    details: errorInfo.details,
    timestamp: new Date().toISOString(),
  });

  // Send to Slack if configured
  if (global.config?.slackMessage) {
    const slackMessage = `[Expense Error] ${context} (${errorInfo.code}): ${errorInfo.message}\n${
      error instanceof Error ? error.stack : error
    }\nContext: ${JSON.stringify(errorInfo.details)}`;
    try {
      global.config.slackMessage(slackMessage);
    } catch (slackError) {
      console.error("Failed to send error to Slack:", slackError);
    }
  }

  // Log activity if configured
  if (global.config?.activityLog) {
    try {
      global.config.activityLog(
        window.location.href,
        "Expense",
        `Error: ${errorInfo.message} (${errorInfo.code})`
      );
    } catch (activityError) {
      console.error("Failed to log activity:", activityError);
    }
  }

  return errorInfo;
};

/**
 * Create a toast error notification with specific message
 * @param {Error|string} error - The error
 * @param {string} context - The context
 * @param {Object} options - Additional toast options
 */
export const showErrorToast = (error, context = "Expense operation", options = {}) => {
  const errorInfo = getErrorMessage(error, context);
  
  toast.error(errorInfo.message, {
    position: "bottom-right",
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    ...options,
  });

  return errorInfo;
};

/**
 * Handle API errors with specific messages and recovery actions
 * @param {Error} error - The API error
 * @param {string} operation - The operation being performed
 * @param {Object} options - Options for error handling
 * @param {Function} options.onRecover - Recovery function to call
 * @param {Function} options.onRetry - Retry function
 * @param {boolean} options.showToast - Whether to show toast notification
 * @returns {Object} Error info and whether it was handled
 */
export const handleApiError = (error, operation, options = {}) => {
  const {
    onRecover,
    onRetry,
    showToast = true,
  } = options;

  const errorInfo = getErrorMessage(error, operation);

  // Handle network errors with retry
  if (
    errorInfo.code === EXPENSE_ERROR_CODES.NETWORK_ERROR ||
    errorInfo.code === EXPENSE_ERROR_CODES.TIMEOUT_ERROR
  ) {
    if (onRetry) {
      // Attempt retry after delay
      setTimeout(() => {
        onRetry();
      }, 2000);
    }
    if (showToast) {
      showErrorToast(error, operation);
    }
    return { ...errorInfo, handled: true, recovered: onRetry !== undefined };
  }

  // Handle server errors
  if (
    errorInfo.code === EXPENSE_ERROR_CODES.API_SERVER_ERROR ||
    errorInfo.code === EXPENSE_ERROR_CODES.API_SERVICE_UNAVAILABLE
  ) {
    if (showToast) {
      showErrorToast(error, operation, { autoClose: 6000 });
    }
    return { ...errorInfo, handled: true, recovered: false };
  }

  // Handle validation errors
  if (errorInfo.code === EXPENSE_ERROR_CODES.API_VALIDATION) {
    if (showToast) {
      showErrorToast(error, operation, { autoClose: 4000 });
    }
    return { ...errorInfo, handled: true, recovered: false };
  }

  // Default handling
  if (showToast) {
    showErrorToast(error, operation);
  }
  
  return { ...errorInfo, handled: true, recovered: false };
};

/**
 * Wrap async function with error handling
 * @param {Function} asyncFn - Async function to wrap
 * @param {string} context - Context for error messages
 * @param {Object} options - Error handling options
 * @returns {Function} Wrapped function
 */
export const withErrorHandling = (asyncFn, context, options = {}) => {
  return async (...args) => {
    try {
      return await asyncFn(...args);
    } catch (error) {
      const errorInfo = handleError(error, context, options.additionalInfo);
      
      if (options.showToast !== false) {
        showErrorToast(error, context, options.toastOptions);
      }
      
      if (options.onError) {
        options.onError(errorInfo);
      }
      
      if (options.rethrow !== false) {
        throw error;
      }
      
      return null;
    }
  };
};
